import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Plus,
  DollarSign,
  Pencil,
  Trash2,
  Check,
  X,
  Tag,
  Percent,
  Calendar,
  Save,
  FileText,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import {
  UPSERT_SERVICE_CONFIG,
  CREATE_CUSTOM_SKU,
  UPDATE_CUSTOM_SKU,
  DELETE_CUSTOM_SKU,
  type OrganizationServiceConfig,
  type OrganizationCustomSku,
} from '@/graphql/organizationPricing';

interface ServicesTabProps {
  clientId: string;
  serviceConfig: OrganizationServiceConfig | null;
  customSkus: OrganizationCustomSku[];
  onRefetch: () => void;
}

export function ServicesTab({
  clientId,
  serviceConfig,
  customSkus,
  onRefetch,
}: ServicesTabProps) {
  // PSA Config state
  const [psaEffective, setPsaEffective] = useState(serviceConfig?.psaEffectiveDate || '');
  const [psaExpiration, setPsaExpiration] = useState(serviceConfig?.psaExpirationDate || '');
  const [discountPercent, setDiscountPercent] = useState(
    serviceConfig?.servicesDiscountPercent?.toString() || '0'
  );
  const [psaNotes, setPsaNotes] = useState(serviceConfig?.notes || '');
  const [psaDirty, setPsaDirty] = useState(false);

  // Custom SKU editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState('');

  // Add custom SKU modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [formSku, setFormSku] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formUnit, setFormUnit] = useState('one-time');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Mutations
  const [upsertConfig, { loading: configSaving }] = useMutation(UPSERT_SERVICE_CONFIG);
  const [createCustomSku, { loading: creatingSku }] = useMutation(CREATE_CUSTOM_SKU);
  const [updateCustomSku] = useMutation(UPDATE_CUSTOM_SKU);
  const [deleteCustomSku] = useMutation(DELETE_CUSTOM_SKU);

  // PSA field handlers
  const handlePsaFieldChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setter(e.target.value);
    setPsaDirty(true);
  };

  const handleSavePsaConfig = async () => {
    try {
      const discount = parseFloat(discountPercent);
      await upsertConfig({
        variables: {
          input: {
            organizationId: clientId,
            servicesDiscountPercent: !isNaN(discount) ? discount : 0,
            psaEffectiveDate: psaEffective || null,
            psaExpirationDate: psaExpiration || null,
            notes: psaNotes || null,
          },
        },
      });
      setPsaDirty(false);
      onRefetch();
    } catch (error) {
      console.error('Failed to save PSA config:', error);
    }
  };

  // Custom SKU editing handlers
  const startEditing = (sku: OrganizationCustomSku) => {
    setEditingId(sku.id);
    setEditingPrice(sku.price.toString());
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingPrice('');
  };

  const saveCustomSkuPrice = async (skuId: string) => {
    const newPrice = parseFloat(editingPrice.replace(/[^0-9.]/g, ''));
    if (!isNaN(newPrice) && newPrice >= 0) {
      try {
        await updateCustomSku({
          variables: {
            id: skuId,
            input: { price: newPrice },
          },
        });
        onRefetch();
      } catch (error) {
        console.error('Failed to update custom SKU price:', error);
      }
    }
    setEditingId(null);
    setEditingPrice('');
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent, skuId: string) => {
    if (e.key === 'Enter') {
      saveCustomSkuPrice(skuId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  // Add custom SKU handler
  const resetAddForm = () => {
    setFormSku('');
    setFormName('');
    setFormDescription('');
    setFormPrice('');
    setFormUnit('one-time');
  };

  const handleAddCustomSku = async () => {
    if (!formName || !formSku) return;

    try {
      await createCustomSku({
        variables: {
          input: {
            organizationId: clientId,
            sku: formSku,
            name: formName,
            description: formDescription || undefined,
            price: parseFloat(formPrice) || 0,
            unit: formUnit,
          },
        },
      });
      setShowAddModal(false);
      resetAddForm();
      onRefetch();
    } catch (error) {
      console.error('Failed to create custom SKU:', error);
    }
  };

  // Delete custom SKU handler
  const handleDeleteCustomSku = async (skuId: string) => {
    try {
      await deleteCustomSku({ variables: { id: skuId } });
      setShowDeleteConfirm(null);
      onRefetch();
    } catch (error) {
      console.error('Failed to delete custom SKU:', error);
    }
  };

  // Check PSA status
  const now = new Date();
  const psaActive = psaEffective && psaExpiration
    ? new Date(psaEffective) <= now && new Date(psaExpiration) >= now
    : false;

  const currentDiscount = parseFloat(discountPercent) || 0;

  return (
    <div className="space-y-6">
      {/* PSA Agreement Card */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Professional Services Agreement (PSA)
            {psaActive && (
              <span className="badge-secondary text-xs bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua">
                Active
              </span>
            )}
          </h3>
          {psaDirty && (
            <button
              onClick={handleSavePsaConfig}
              disabled={configSaving}
              className="btn-primary btn-sm"
            >
              <Save className="w-4 h-4" />
              {configSaving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                Effective Date
              </label>
              <input
                type="date"
                value={psaEffective}
                onChange={handlePsaFieldChange(setPsaEffective)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                Expiration Date
              </label>
              <input
                type="date"
                value={psaExpiration}
                onChange={handlePsaFieldChange(setPsaExpiration)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Notes
              </label>
              <input
                type="text"
                value={psaNotes}
                onChange={handlePsaFieldChange(setPsaNotes)}
                placeholder="Agreement notes..."
                className="input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Services Discount Card */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Services Discount
          </h3>
          {psaDirty && (
            <button
              onClick={handleSavePsaConfig}
              disabled={configSaving}
              className="btn-primary btn-sm"
            >
              <Save className="w-4 h-4" />
              {configSaving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Discount Percentage
              </label>
              <div className="flex items-center gap-2">
                <div className="relative w-32">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={discountPercent}
                    onChange={(e) => {
                      setDiscountPercent(e.target.value);
                      setPsaDirty(true);
                    }}
                    className="input text-right pr-8"
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                </div>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                {currentDiscount > 0 ? (
                  <>
                    A <span className="font-semibold text-turquoise dark:text-aqua">{currentDiscount}%</span> discount
                    is applied to all Services category SKUs for this client.
                  </>
                ) : (
                  'No discount is currently applied. Set a percentage to apply a discount to all Services SKUs.'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Client-Specific Service SKUs */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="font-semibold text-slate dark:text-mist flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Client-Specific Service SKUs
            <span className="badge-secondary text-xs">{customSkus.length}</span>
          </h3>
          <button onClick={() => setShowAddModal(true)} className="btn-primary btn-sm">
            <Plus className="w-4 h-4" />
            Add Custom SKU
          </button>
        </div>

        {customSkus.length === 0 ? (
          <div className="card-body text-center text-chalkboard dark:text-rain-cloud py-8">
            No custom service SKUs defined. Click &quot;Add Custom SKU&quot; to create
            client-specific specialty services.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Unit</th>
                  <th className="text-right">Price</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customSkus.map((sku) => (
                  <tr key={sku.id} className="group">
                    <td>
                      <span className="font-mono text-xs text-chalkboard dark:text-rain-cloud">
                        {sku.sku}
                      </span>
                    </td>
                    <td>
                      <div>
                        <p className="font-medium text-slate dark:text-mist">{sku.name}</p>
                        {sku.description && (
                          <p className="text-xs text-chalkboard dark:text-rain-cloud">
                            {sku.description}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="text-chalkboard dark:text-rain-cloud text-sm">
                      {sku.unit}
                    </td>
                    <td className="text-right">
                      {editingId === sku.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <div className="relative w-28">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                            <input
                              type="text"
                              value={editingPrice}
                              onChange={(e) => setEditingPrice(e.target.value)}
                              onKeyDown={(e) => handlePriceKeyDown(e, sku.id)}
                              className="input pl-7 py-1 text-right text-sm"
                              autoFocus
                            />
                          </div>
                          <button
                            onClick={() => saveCustomSkuPrice(sku.id)}
                            className="btn-ghost btn-icon btn-sm text-turquoise dark:text-aqua"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium text-violet dark:text-violet-light">
                          {formatCurrency(sku.price)}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingId !== sku.id && (
                        <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => startEditing(sku)}
                            className="btn-ghost btn-icon btn-sm"
                            title="Edit price"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(sku.id)}
                            className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                            title="Delete custom SKU"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Custom SKU Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetAddForm();
        }}
        title="Add Custom Service SKU"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                SKU Code
              </label>
              <input
                type="text"
                value={formSku}
                onChange={(e) => setFormSku(e.target.value)}
                placeholder="SVC-CUST-001"
                className="input font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Unit
              </label>
              <select
                value={formUnit}
                onChange={(e) => setFormUnit(e.target.value)}
                className="select"
              >
                <option value="one-time">One-time</option>
                <option value="per month">Per Month</option>
                <option value="per hour">Per Hour</option>
                <option value="per day">Per Day</option>
                <option value="per session">Per Session</option>
                <option value="per project">Per Project</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Name
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Enter service name"
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Description
            </label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Enter service description"
              rows={2}
              className="input resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Price
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-chalkboard dark:text-rain-cloud" />
              <input
                type="text"
                value={formPrice}
                onChange={(e) => setFormPrice(e.target.value)}
                placeholder="0.00"
                className="input pl-8"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => {
                setShowAddModal(false);
                resetAddForm();
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCustomSku}
              className="btn-primary"
              disabled={!formName || !formSku || creatingSku}
            >
              <Plus className="w-4 h-4" />
              {creatingSku ? 'Adding...' : 'Add Custom SKU'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(null)}
        title="Delete Custom SKU"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to delete this custom service SKU? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => showDeleteConfirm && handleDeleteCustomSku(showDeleteConfirm)}
              className="btn-primary bg-rust hover:bg-rust/90"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
